import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore } from '../../stores/useBookingFlowStore';
import ProgressBar from '../../components/booking/ProgressBar';

const ConfirmationPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const {
    service,
    dateTime,
    staff,
    customerInfo,
    bookingId,
    managementToken,
    clearAll,
  } = useBookingFlowStore();

  // 驗證是否有預約結果
  useEffect(() => {
    if (!bookingId || !managementToken) {
      navigate(`/book/${storeId}`);
    }
  }, [bookingId, managementToken, navigate, storeId]);

  const handleNewBooking = () => {
    clearAll();
    navigate(`/book/${storeId}`);
  };

  const handleViewBooking = () => {
    if (managementToken) {
      navigate(`/book/${storeId}/my-bookings/${managementToken}`);
    }
  };

  const managementUrl = managementToken
    ? `${window.location.origin}/book/${storeId}/my-bookings/${managementToken}`
    : '';

  if (!bookingId || !service || !dateTime || !customerInfo) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 進度條 */}
        <ProgressBar
          currentStep={4}
          totalSteps={4}
          steps={[
            t('pages:booking.storeHome.selectService'),
            t('pages:booking.dateTime.title'),
            t('pages:booking.info.title'),
            t('pages:booking.confirmation.successTitle')
          ]}
        />

        {/* 成功訊息 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8 mb-6 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.confirmation.successTitle')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('pages:booking.confirmation.successMessage')}
          </p>
        </div>

        {/* 預約詳細資訊 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('pages:booking.confirmation.bookingDetails')}
          </h2>

          <div className="space-y-4">
            {/* 預約編號 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.bookingNumber')}</span>
              <span className="font-semibold text-gray-900 dark:text-white font-mono">
                {bookingId}
              </span>
            </div>

            {/* 服務項目 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.serviceItem')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {service.name}
              </span>
            </div>

            {/* 預約時間 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.bookingTime')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {dateTime.date} {dateTime.displayTime}
              </span>
            </div>

            {/* 服務時長 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.serviceDuration')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {service.duration} {t('translation:common.time.minutes')}
              </span>
            </div>

            {/* 服務價格 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.servicePrice')}</span>
              <span className="font-semibold text-gray-900 dark:text-white text-xl">
                ${Math.floor(service.price)}
              </span>
            </div>

            {/* 服務人員 */}
            {staff && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.staffMember')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {staff.name}
                </span>
              </div>
            )}

            {/* 聯絡姓名 */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.contactName')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {customerInfo.name}
              </span>
            </div>

            {/* 聯絡 Email */}
            <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.contactEmail')}</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {customerInfo.email}
              </span>
            </div>

            {/* 聯絡電話 */}
            {customerInfo.phone && (
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">{t('pages:booking.confirmation.contactPhone')}</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {customerInfo.phone}
                </span>
              </div>
            )}

            {/* 備註 */}
            {customerInfo.note && (
              <div className="py-3">
                <span className="text-gray-600 dark:text-gray-400 block mb-2">{t('pages:booking.confirmation.note')}</span>
                <p className="text-gray-900 dark:text-white bg-gray-50 dark:bg-slate-700 p-3 rounded">
                  {customerInfo.note}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 管理連結 */}
        <div className="bg-brand-50 dark:bg-brand-900/20 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.confirmation.manageBooking')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('pages:booking.confirmation.saveLink')}
          </p>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('pages:booking.confirmation.managementLink')}</p>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={managementUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded text-sm text-gray-700 dark:text-gray-300 font-mono"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(managementUrl);
                }}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded font-semibold text-sm transition-colors"
              >
                {t('pages:booking.confirmation.copy')}
              </button>
            </div>
          </div>
          <button
            onClick={handleViewBooking}
            className="w-full px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-bold transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {t('pages:booking.confirmation.viewMyBooking')}
          </button>
        </div>

        {/* 操作按鈕 */}
        <div className="flex justify-center">
          <button
            onClick={handleNewBooking}
            className="px-8 py-3 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg font-bold border-2 border-gray-300 dark:border-slate-600 hover:border-brand-500 dark:hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition-all duration-200"
          >
            {t('pages:booking.confirmation.backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationPage;
